import React, { useState, useRef, useEffect, useContext } from 'react'
import { oneOf, func, bool, string } from 'prop-types'

// components
import { SliderMenu } from './partials'
import { GeneralInformationCompare } from 'components/molecules'

// helpers && libs
import * as Styled from './CompareSlider.styles'
import { useSelector } from 'react-redux'
import { DetailsMapper } from 'containers/Compare/partials'
import { Loader } from 'components/atoms'
import { TextContext } from 'hocs'
import MessageBox from '../MessageBox/MessageBox'
import { BOOKABLE_TYPES, entryPointId } from 'settings/app/constants'

const CompareSlider = (props) => {
  const {
    selectedType,
    getObjectById,
    removeItemFromBookmarks,
    compareWithOutSearchTerm,
    getBackPath,
    openAccordionKey,
    setOpenAccordionKey,
    disableToggleBookmark,
    ...rest
  } = props
  const state = useSelector((state) => state)
  const { compare: CompareState } = state
  const { activeIds } = CompareState.activeElements

  // all results depending on selected typ (hospital or doctors)
  const [activeId, setActiveId] = useState(activeIds[0])
  const [viewPortWidth, setViewPortWidth] = useState(0)
  const sliderRef = useRef()

  const textContext = useContext(TextContext)

  // touch positions
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 })
  const [touchEnd, setTouchEnd] = useState({ x: 0, y: 0 })

  // set new active slider item on bookmark remove
  useEffect(() => {
    setActiveId(activeIds[0])
  }, [activeIds])

  // resize event && initial width set
  React.useEffect(() => {
    const onResize = () => {
      setViewPortWidth(
        Math.max(
          document.documentElement.clientWidth || 0,
          window.innerWidth || 0
        )
      )
    }

    onResize()
    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [])

  // set container styles
  useEffect(() => {
    if (typeof sliderRef.current !== 'undefined') {
      sliderRef.current.style.width = `${ viewPortWidth - 10 }px`
    }
  }, [viewPortWidth])

  // animation styles
  const innerStyle = {
    width: `${ activeIds.length * viewPortWidth }px`,
    left: `${ activeIds.indexOf(activeId) * viewPortWidth * -1 }px`,
  }
  const itemWidth = {
    width: `${ viewPortWidth }px`,
    paddingRight: selectedType === entryPointId.HOSPITALS ? '25px' : '15px',
  }

  // handle swipe event
  const handleTouch = (e, resultId) => {
    if (typeof e.touches !== 'undefined') {
      // eslint-disable-next-line prefer-destructuring
      const touch = e.touches[0]

      switch (e.type) {
        case 'touchstart':
          setTouchStart({ x: touch.pageX, y: touch.pageY })
          break

        case 'touchmove':
          setTouchEnd({ x: touch.pageX, y: touch.pageY })
          break

        case 'touchend':
          const curIndex = activeIds.indexOf(resultId)
          const swipeDirection = touchEnd.x > touchStart.x ? 'left' : 'right'
          const deltaY = Math.abs(touchEnd.y - touchStart.y)

          // delta value avoid swiping on scroll vertical (max. 30 px difference allowed)
          if (deltaY < 30) {
            if (swipeDirection === 'left') {
              if (curIndex !== 0) {
                setTouchStart({ x: 0, y: 0 })
                setTouchEnd({ x: 0, y: 0 })
                setActiveId(activeIds[curIndex - 1])
              }
            } else {
              if (curIndex < activeIds.length - 1) {
                setTouchStart({ x: 0, y: 0 })
                setTouchEnd({ x: 0, y: 0 })
                setActiveId(activeIds[curIndex + 1])
              }
            }
          }
          break

        default:
          return false
      }
    }
  }

  return (
    <Styled.CompareSlider { ...rest } data-cy='slider-container'>
      <SliderMenu
        slideIds={ activeIds }
        activeId={ activeId }
        handleActiveIdChange={ (itemId) => setActiveId(itemId) }
      />

      <div className='slider-container-outer' ref={ sliderRef }>
        <div className='slider-container-inner' style={ innerStyle }>
          {activeIds.map((item, index) => {
            const result = getObjectById(item)
            return (
              <div className='slider-item' key={ index } style={ itemWidth }>
                <div
                  onTouchStart={ (event) => handleTouch(event, result.id) }
                  onTouchMove={ (event) => handleTouch(event, result.id) }
                  onTouchEnd={ (event) => handleTouch(event, result.id) }>
                  {/* header */}
                  <GeneralInformationCompare
                    mobile={ true }
                    type={ 'header' }
                    data={ result }
                    resultType={ selectedType }
                    onToggleBookmark={ (item) => removeItemFromBookmarks(item) }
                    disableToggleBookmark={ disableToggleBookmark }
                    withOutTreatmentReason={ compareWithOutSearchTerm }
                  />
                </div>

                {/* comparing data */}
                {selectedType !== entryPointId.DOCTORS && (
                  <GeneralInformationCompare
                    mobile={ true }
                    type={ 'compare' }
                    data={ result }
                    resultType={ selectedType }
                    onToggleBookmark={ (item) => removeItemFromBookmarks(item) }
                  />
                )}

                {/* contact data over compare data  */}
                <GeneralInformationCompare
                  mobile={ true }
                  type={ 'contact' }
                  data={ result }
                  resultType={ selectedType }
                  onToggleBookmark={ (item) => removeItemFromBookmarks(item) }
                />

                {/* details data */}
                {!!result?.details ? (
                  Object.keys(result.details).length > 0 ? (
                    <DetailsMapper
                      key={ index }
                      selectedType={ selectedType }
                      result={ result }
                      isTreatmentReason={ false }
                      backLink={ getBackPath }
                      openAccordionKey={ openAccordionKey }
                      setOpenAccordionKey={ (accordionKey) =>
                        setOpenAccordionKey(accordionKey)
                      }
                    />
                  ) : (
                    <div className='error-message'>
                      <MessageBox isError={ true } />
                    </div>
                  )
                ) : (
                  <Loader variant='loading'>
                    {textContext['general.loadingData']}
                  </Loader>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </Styled.CompareSlider>
  )
}

CompareSlider.propTypes = {
  selectedType: oneOf(BOOKABLE_TYPES).isRequired,
  // handler
  getObjectById: func.isRequired,
  removeItemFromBookmarks: func.isRequired,
  setOpenAccordionKey: func.isRequired,
  // flags
  compareWithOutSearchTerm: bool.isRequired,
  getBackPath: func.isRequired,
  openAccordionKey: string.isRequired,
  disableToggleBookmark: bool.isRequired,
}

export default CompareSlider
